import { useState, useEffect } from 'react';
import axios from 'axios';
import { AgentCard } from '../components/AgentCard';
import { Award, TrendingUp, Star, Shield, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TopPerformersPage = () => {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTopPerformers();
    }, []);

    const fetchTopPerformers = async () => {
        try {
            const response = await axios.get(`${API}/agents?top_performers_only=true`);
            setAgents(response.data.agents || response.data);
        } catch (error) {
            console.error('Error fetching top performers:', error);
        } finally {
            setLoading(false);
        }
    };

    const criteria = [
        {
            icon: <Star className="w-6 h-6" />,
            title: 'Note minimale 4.5/5',
            description: 'Basée sur les avis clients vérifiés',
        },
        {
            icon: <TrendingUp className="w-6 h-6" />,
            title: '5+ avis vérifiés',
            description: 'Des clients réels et satisfaits',
        },
        {
            icon: <Shield className="w-6 h-6" />,
            title: '80%+ taux de réponse',
            description: 'Réactivité garantie',
        },
    ];

    return (
        <div className="min-h-screen pt-24 pb-16" data-testid="top-performers-page">
            {/* Hero */}
            <div className="bg-gradient-to-b from-scalimmo-sand to-white py-16 mb-10">
                <div className="container-scalimmo text-center">
                    <span className="elite-badge inline-flex mb-6">
                        <Award className="w-4 h-4" />
                        Programme exclusif
                    </span>

                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-scalimmo-navy mb-6">
                        Nos Top Performers
                    </h1>

                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                        Découvrez l'élite des agents immobiliers. Ces professionnels exceptionnels
                        ont prouvé leur excellence par leurs performances et la satisfaction de leurs clients.
                    </p>
                </div>
            </div>

            <div className="container-scalimmo">
                {/* Criteria */}
                <div className="bg-white rounded-xl border border-scalimmo-gold/20 p-8 mb-12 shadow-soft">
                    <h2 className="font-heading text-2xl font-semibold text-scalimmo-navy mb-6 text-center">
                        Critères de certification
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                        {criteria.map((item, idx) => (
                            <div key={idx} className="text-center">
                                <div className="w-14 h-14 bg-scalimmo-gold/10 rounded-full flex items-center justify-center mx-auto mb-4 text-scalimmo-gold">
                                    {item.icon}
                                </div>
                                <h3 className="font-heading text-lg font-semibold text-scalimmo-navy mb-1">
                                    {item.title}
                                </h3>
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Agents Grid */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-scalimmo-gold" />
                    </div>
                ) : agents.length === 0 ? (
                    <div className="text-center py-20">
                        <Award className="w-16 h-16 text-scalimmo-gold/30 mx-auto mb-4" />
                        <h3 className="font-heading text-xl font-semibold text-scalimmo-navy mb-2">
                            Aucun Top Performer pour le moment
                        </h3>
                        <p className="text-muted-foreground">
                            Les premiers agents certifiés apparaîtront bientôt ici
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {agents.map((agent) => (
                            <AgentCard key={agent.id} agent={agent} variant="elite" />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TopPerformersPage;
